import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from '@/lib/query';

/**
 * Hooks für das Wartungsfenster pro Kunde ("maintenance session").
 *
 * Das Fenster - nicht der einzelne Lauf - taktet die Kundenmails:
 *   Fenster auf  -> genau eine Mail "Wartung gestartet"
 *   Fenster zu   -> genau eine Mail "Wartung abgeschlossen"
 *   Fehler drin  -> Fenster bleibt offen, KEINE Mail; Colin schließt von Hand
 *
 * Backend-Endpunkte:
 *   GET  /customers/{id}/maintenance-session
 *   POST /customers/{id}/maintenance-session/open   { silent?, notes? }
 *   POST /customers/{id}/maintenance-session/close  { silent?, force? }
 *
 * Close antwortet 409 in ZWEI Fällen, solange `force` fehlt - beide sind ein
 * gewollter Stopp, kein Bug, und die UI muss beide erklären und sich `force`
 * bewusst bestätigen lassen (siehe MaintenanceSessionCard):
 *   had_failures        - ein Lauf ist auf dem Kundensystem gescheitert.
 *   runs_blocked_offen  - ein Lauf hat das Kundensystem nie erreicht und
 *                         wurde seither nicht nachgeholt.
 *
 * Typen sind bewusst lokal definiert (api.gen.ts wird zentral vom Gate-Agent
 * gepflegt).
 *
 * WICHTIG - Wire-Format != View-Format. Das Backend liefert (siehe
 * backend/app/api/schemas/maintenance_session.py):
 *   status:   'offen' | 'abgeschlossen'   (deutsch, DB-CHECK-Constraint)
 *   counters: { runs_total, runs_active, runs_success, runs_failed,
 *               runs_blocked, runs_blocked_offen }
 *   runs[]:   { routine, routine_code, systeme, ... }
 * `normalisiere` übersetzt das einmal an dieser Stelle. Ohne die Umsetzung
 * war `isSessionOpen` immer false: die Karte zeigte bei OFFENEM Fenster
 * "Keine Wartung offen" samt Öffnen-Knopf, und Schließen war im UI gar
 * nicht erreichbar. Die englischen/deutschen Feldnamen werden beide
 * akzeptiert, damit ein Umbenennen auf einer Seite nicht sofort blind macht.
 */

// --- Wire-Modell -------------------------------------------------------------

export type MaintenanceSessionStatus = 'open' | 'closed';

export interface MaintenanceSession {
  id: string;
  /** 'open' | 'closed'; Backend liefert string, wir mappen defensiv. */
  status: string;
  opened_at: string;
  closed_at: string | null;
  /** true = automatisch beim ersten Lauf aufgegangen, false = von Hand. */
  auto_opened: boolean;
  had_failures: boolean;
  started_mail_sent_at: string | null;
  finished_mail_sent_at: string | null;
  /**
   * Notiz des Fensters. Seit 16.09.2026 geht sie als Hinweis-Zeile in die
   * Kundenmails "Wartung gestartet" und "Wartung abgeschlossen".
   */
  notes: string | null;
  /**
   * Von Hand gewartete Systeme (IDs). Am offenen Fenster per PATCH
   * gepflegt; zählen in der Abschlussmail mit und bekommen beim Schließen
   * den Wartungsstand. Optional, weil ältere Payloads das Feld nicht tragen.
   */
  manuelle_systeme?: string[];
}

export interface MaintenanceSessionRun {
  id: string;
  routine_display_name: string;
  /** Run-Status als string (siehe RunStatusBadge). */
  status: string;
  /** Anzahl der Systeme im Lauf; eine Namensliste wird ebenfalls gerendert. */
  systeme: number | string[] | null;
}

export interface MaintenanceSessionCounts {
  gesamt: number;
  laufend: number;
  /** Auf dem Kundensystem gescheitert. Enthält `blockiert` NICHT. */
  fehlgeschlagen: number;
  erfolgreich: number;
  /**
   * Blockiert = kein Zugang, der Lauf hat NIE STATTGEFUNDEN. Das ist kein
   * Fehlschlag: am Kundensystem ist nichts passiert.
   *
   * HISTORIENZAHL - sie fällt nie wieder auf 0. Ein Wiederholungslauf ist
   * ein neuer Run; der blockierte bleibt mit seinem Status am Fenster
   * hängen. Taugt für die Anzeige, NICHT für eine Entscheidung.
   */
  blockiert: number;
  /**
   * Davon heute noch unerledigt (Wire: runs_blocked_offen): blockierte
   * Läufe, deren Zielsysteme im selben Fenster noch kein erfolgreicher Lauf
   * gewartet hat. NUR diese Zahl darf das UI zur Entscheidung heranziehen -
   * sie hält Auto-Close und Schließen an, und an ihr hängt der 409.
   */
  unerledigt: number;
}

export interface MaintenanceSessionResponse {
  session: MaintenanceSession | null;
  runs: MaintenanceSessionRun[];
  counts: MaintenanceSessionCounts;
}

/**
 * Antwort von open/close: das Fenster FLACH, plus zwei Betriebsinfos - nicht
 * die Form von GET. `mail_due` heißt hier "durch diesen Aufruf ist eine
 * Kundenmail entstanden".
 */
export interface MaintenanceSessionActionResult extends Omit<MaintenanceSession, 'status'> {
  status: string;
  created: boolean;
  mail_due: boolean;
}

const EMPTY_COUNTS: MaintenanceSessionCounts = {
  gesamt: 0,
  laufend: 0,
  fehlgeschlagen: 0,
  erfolgreich: 0,
  blockiert: 0,
  unerledigt: 0,
};

// --- Wire -> View ------------------------------------------------------------

/** Rohform, wie sie GET /customers/{id}/maintenance-session liefert. */
interface WireSession extends Omit<MaintenanceSession, 'status'> {
  status: string;
}

interface WireRun extends Partial<MaintenanceSessionRun> {
  id: string;
  status: string;
  routine?: string | null;
  routine_code?: string | null;
}

interface WireCounters {
  runs_total?: number;
  runs_active?: number;
  runs_success?: number;
  runs_failed?: number;
  /** Je blockiert gewesen (Historie). */
  runs_blocked?: number;
  /** Davon noch offen - der einzige Schalter für die Schliess-Schranke. */
  runs_blocked_offen?: number;
}

interface WirePayload {
  session?: WireSession | null;
  runs?: WireRun[];
  counters?: WireCounters;
  counts?: Partial<MaintenanceSessionCounts>;
}

function normalisiereStatus(status: string): string {
  if (status === 'offen') return 'open';
  if (status === 'abgeschlossen') return 'closed';
  return status;
}

function normalisiereRun(run: WireRun): MaintenanceSessionRun {
  return {
    id: run.id,
    status: run.status,
    // routine_display_name gibt es im Wire-Format nicht; der Code ist die
    // ehrlichste Notlösung, "Routine" wäre eine Erfindung.
    routine_display_name:
      run.routine_display_name ?? run.routine ?? run.routine_code ?? 'Unbekannte Routine',
    systeme: run.systeme ?? null,
  };
}

function normalisiereCounts(
  raw: WirePayload,
  runs: MaintenanceSessionRun[]
): MaintenanceSessionCounts {
  if (raw.counters) {
    return {
      gesamt: raw.counters.runs_total ?? runs.length,
      laufend: raw.counters.runs_active ?? 0,
      fehlgeschlagen: raw.counters.runs_failed ?? 0,
      erfolgreich: raw.counters.runs_success ?? 0,
      blockiert: raw.counters.runs_blocked ?? 0,
      // Fehlt das Feld (alter Server), ist "unerledigt" 0 und die Schranke
      // greift nicht - das ist der Zustand von vorher, nicht schlechter.
      // Erfunden werden darf sie nicht: aus `runs_blocked` lässt sich nicht
      // ableiten, ob die Arbeit inzwischen nachgeholt wurde.
      unerledigt: raw.counters.runs_blocked_offen ?? 0,
    };
  }
  if (raw.counts) return { ...EMPTY_COUNTS, ...raw.counts };
  return { ...EMPTY_COUNTS, gesamt: runs.length };
}

export function normalisiere(raw: WirePayload): MaintenanceSessionResponse {
  const wire = raw.session ?? null;
  const runs = (raw.runs ?? []).map(normalisiereRun);
  return {
    session: wire ? { ...wire, status: normalisiereStatus(wire.status) } : null,
    runs,
    counts: normalisiereCounts(raw, runs),
  };
}

// --- Query-Keys --------------------------------------------------------------

export const maintenanceSessionKeys = {
  all: ['maintenance-session'] as const,
  byCustomer: (customerId: string) => ['maintenance-session', customerId] as const,
};

// --- Helfer ------------------------------------------------------------------

export function isSessionOpen(session: MaintenanceSession | null | undefined): boolean {
  return session?.status === 'open';
}

/**
 * 409 = "die Wartung ist nicht erledigt" - der einzige Fall, der `force`
 * braucht. Zwei Auslöser: ein Lauf ist gescheitert ODER ein Lauf hat das
 * Kundensystem nie erreicht. Welcher es war, steht im detail-Text.
 */
export function isSessionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/** Begründung des Backends zum 409, falls es eine mitschickt. */
export function sessionConflictDetail(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.problem?.detail ?? error.problem?.title ?? null;
}

// --- Query -------------------------------------------------------------------

export function useMaintenanceSession(
  customerId: string | undefined
): ReturnType<typeof useQuery<MaintenanceSessionResponse>> {
  return useQuery<MaintenanceSessionResponse>({
    queryKey: maintenanceSessionKeys.byCustomer(customerId ?? ''),
    queryFn: async () => {
      const raw = await api.get<WirePayload>(`/customers/${customerId ?? ''}/maintenance-session`);
      return normalisiere(raw);
    },
    enabled: Boolean(customerId),
    // Offenes Fenster mit laufenden Runs: schnell nachziehen, damit der
    // Abschluss-Knopf nicht auf veralteten Zählern sitzt.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || !isSessionOpen(data.session)) return false;
      return data.counts.laufend > 0 ? 5_000 : 30_000;
    },
    staleTime: 5_000,
  });
}

// --- Mutations ---------------------------------------------------------------

export interface OpenMaintenanceSessionInput {
  customerId: string;
  /** true = Fenster aufmachen, ohne dass der Kunde eine Mail bekommt. */
  silent?: boolean;
  notes?: string;
}

export interface CloseMaintenanceSessionInput {
  customerId: string;
  /** true = Fenster schließen, ohne dass der Kunde eine Mail bekommt. */
  silent?: boolean;
  /**
   * true = trotz fehlgeschlagener ODER offener blockierter Läufe schließen
   * (hängt an beiden 409-Fällen).
   */
  force?: boolean;
  /** Anmerkung an den Kunden, eigener Absatz in der Abschlussmail (z. B. Komplikationen). */
  anmerkung?: string;
}

interface OpenBody {
  silent?: boolean;
  notes?: string;
}

interface CloseBody {
  silent?: boolean;
  force?: boolean;
  anmerkung?: string;
}

function invalidateAfterSessionChange(
  qc: ReturnType<typeof useQueryClient>,
  customerId: string
): void {
  void qc.invalidateQueries({ queryKey: maintenanceSessionKeys.byCustomer(customerId) });
  // Stats hängen unter dem detail-Präfix (last_maintenance_at).
  void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
  // Open/Close erzeugt genau die Mail, die in der Benachrichtigungsliste steht.
  void qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
}

export function useOpenMaintenanceSession(): ReturnType<
  typeof useMutation<MaintenanceSessionActionResult, Error, OpenMaintenanceSessionInput>
> {
  const qc = useQueryClient();
  return useMutation<MaintenanceSessionActionResult, Error, OpenMaintenanceSessionInput>({
    mutationFn: ({ customerId, silent, notes }) => {
      const body: OpenBody = { silent: silent ?? false };
      if (notes) body.notes = notes;
      return api.post<MaintenanceSessionActionResult, OpenBody>(
        `/customers/${customerId}/maintenance-session/open`,
        body
      );
    },
    onSuccess: (_res, vars) => invalidateAfterSessionChange(qc, vars.customerId),
  });
}

export function useCloseMaintenanceSession(): ReturnType<
  typeof useMutation<MaintenanceSessionActionResult, Error, CloseMaintenanceSessionInput>
> {
  const qc = useQueryClient();
  return useMutation<MaintenanceSessionActionResult, Error, CloseMaintenanceSessionInput>({
    mutationFn: ({ customerId, silent, force, anmerkung }) => {
      const body: CloseBody = { silent: silent ?? false };
      // force nur mitschicken, wenn es bewusst bestätigt wurde - ein
      // mitgesendetes force:false würde den 409 nur verschleiern.
      if (force) body.force = true;
      if (anmerkung && anmerkung.trim()) body.anmerkung = anmerkung.trim();
      return api.post<MaintenanceSessionActionResult, CloseBody>(
        `/customers/${customerId}/maintenance-session/close`,
        body
      );
    },
    onSuccess: (_res, vars) => invalidateAfterSessionChange(qc, vars.customerId),
  });
}

export interface UpdateMaintenanceSessionInput {
  customerId: string;
  sessionId: string;
  /** Kompletter neuer Stand der von Hand gewarteten Systeme (kein Delta). */
  manuelleSysteme?: string[];
  /** Notiz setzen; leerer String löscht sie. Weglassen = unverändert. */
  notes?: string;
}

interface UpdateBody {
  manuelle_systeme?: string[];
  notes?: string;
}

/**
 * PATCH /maintenance-sessions/{id}: Notiz und von Hand gewartete Systeme
 * eines OFFENEN Fensters (16.09.2026, Hasselbach: hv01 und video02 werden
 * von Hand gewartet und sollen im laufenden Fenster stehen, statt hinterher
 * als zweites Fenster nacherfasst zu werden). Geschlossene Fenster: 409.
 */
export function useUpdateMaintenanceSession(): ReturnType<
  typeof useMutation<MaintenanceSession, Error, UpdateMaintenanceSessionInput>
> {
  const qc = useQueryClient();
  return useMutation<MaintenanceSession, Error, UpdateMaintenanceSessionInput>({
    mutationFn: ({ sessionId, manuelleSysteme, notes }) => {
      const body: UpdateBody = {};
      if (manuelleSysteme !== undefined) body.manuelle_systeme = manuelleSysteme;
      if (notes !== undefined) body.notes = notes;
      return api.patch<MaintenanceSession, UpdateBody>(`/maintenance-sessions/${sessionId}`, body);
    },
    onSuccess: (_res, vars) => invalidateAfterSessionChange(qc, vars.customerId),
  });
}

// --- Historie ----------------------------------------------------------------

/**
 * Ein Fenster in der Historien-LISTE (GET /maintenance-sessions).
 *
 * Bewusst ohne Zähler: die Liste liefert nur Metadaten. Zahlen zu jedem
 * Fenster gäbe es nur, indem der Server je Zeile die Läufe nachlädt - bei
 * 50 Zeilen also 100 Abfragen. Die Zahlen kommen deshalb erst beim Aufklappen
 * aus `useMaintenanceSessionDetail`.
 */
export interface MaintenanceSessionHistoryItem {
  id: string;
  customer_id: string;
  customer_name: string | null;
  /** 'open' | 'closed' - normalisiert wie oben. */
  status: string;
  opened_at: string;
  closed_at: string | null;
  opened_by: string;
  closed_by: string | null;
  auto_opened: boolean;
  had_failures: boolean;
  started_mail_sent_at: string | null;
  finished_mail_sent_at: string | null;
  notes: string | null;
  /** 'automatisch' = aus Läufen entstanden, 'manuell' = nacherfasst. */
  erfassungsart?: string;
  durchgefuehrt_von?: string | null;
  arbeitsbeschreibung?: string | null;
  /** Anmerkung beim Schließen (z. B. Komplikationen), ging in die Abschlussmail. */
  abschluss_anmerkung?: string | null;
  manuelle_systeme?: string[];
}

export interface MaintenanceSessionDetail {
  session: MaintenanceSessionHistoryItem;
  runs: MaintenanceSessionRun[];
  counts: MaintenanceSessionCounts;
  /** Unterschiedliche Systeme, die tatsächlich betreten wurden. */
  systemeEindeutig: number;
  /** Davon solche, auf denen etwas geändert wurde. */
  systemeGeaendert: number;
  /** Wie viele Bericht-Mails zu diesem Fenster schon erzeugt wurden. */
  berichteErzeugt: number;
  /**
   * Von Hand gewartete Systeme mit Namen (am offenen Fenster eingetragen
   * oder aus der Nacherfassung). Stecken bereits in `systemeEindeutig`.
   */
  manuelleSysteme: ManuellesSystem[];
}

export interface ManuellesSystem {
  id: string;
  name: string;
  hostname: string | null;
}

interface WireHistoryItem extends Omit<MaintenanceSessionHistoryItem, 'status'> {
  status: string;
}

interface WireHistory {
  data?: WireHistoryItem[];
  total?: number;
}

interface WireDetail extends WirePayload {
  session?: WireHistoryItem | null;
  systeme_eindeutig?: number;
  systeme_geaendert?: number;
  berichte_erzeugt?: number;
  manuelle_systeme_details?: ManuellesSystem[];
}

export const maintenanceHistoryKeys = {
  all: ['maintenance-history'] as const,
  byCustomer: (customerId: string) => ['maintenance-history', customerId] as const,
  detail: (sessionId: string) => ['maintenance-history', 'detail', sessionId] as const,
};

/** Historie der Wartungsfenster eines Kunden, jüngstes zuerst. */
export function useMaintenanceHistory(
  customerId: string | undefined,
  limit = 50
): ReturnType<typeof useQuery<MaintenanceSessionHistoryItem[]>> {
  return useQuery<MaintenanceSessionHistoryItem[]>({
    queryKey: maintenanceHistoryKeys.byCustomer(customerId ?? ''),
    queryFn: async () => {
      const raw = await api.get<WireHistory>(
        `/maintenance-sessions?customer_id=${customerId ?? ''}&limit=${limit}`
      );
      return (raw.data ?? []).map((item) => ({
        ...item,
        status: normalisiereStatus(item.status),
      }));
    },
    enabled: Boolean(customerId),
    staleTime: 30_000,
  });
}

/**
 * Ein bestimmtes Fenster samt Läufen - auch ein längst geschlossenes.
 *
 * `enabled` hängt an der sessionId: die Detailabfrage läuft erst, wenn der
 * Betreiber ein Fenster aufklappt. Ohne das würde die Historienseite für
 * jedes gelistete Fenster sofort eine eigene Abfrage auslösen.
 */
export function useMaintenanceSessionDetail(
  sessionId: string | undefined
): ReturnType<typeof useQuery<MaintenanceSessionDetail>> {
  return useQuery<MaintenanceSessionDetail>({
    queryKey: maintenanceHistoryKeys.detail(sessionId ?? ''),
    queryFn: async () => {
      const raw = await api.get<WireDetail>(`/maintenance-sessions/${sessionId ?? ''}`);
      const basis = normalisiere(raw);
      const wire = raw.session;
      if (!wire) throw new Error('Antwort ohne Fenster');
      return {
        session: { ...wire, status: normalisiereStatus(wire.status) },
        runs: basis.runs,
        counts: basis.counts,
        systemeEindeutig: raw.systeme_eindeutig ?? 0,
        systemeGeaendert: raw.systeme_geaendert ?? 0,
        berichteErzeugt: raw.berichte_erzeugt ?? 0,
        manuelleSysteme: raw.manuelle_systeme_details ?? [],
      };
    },
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}

// --- Bericht-Mail ------------------------------------------------------------

export interface MaintenanceReportMailResult {
  notification_id: string;
  /** false = es lag bereits ein unversendeter Entwurf vor. */
  created: boolean;
  status: string;
  subject: string;
  recipients: string[];
  bericht_nr: number;
  /** Der fertig gerenderte Text - genau das, was rausgeht. */
  body: string;
  /** Tatsächliche Empfänger nach Redirect; erst nach dem Versand gefüllt. */
  effective_recipients: string[];
}

/**
 * Erzeugt den Wartungsbericht - je nach `send` als Entwurf oder direkt versendet.
 *
 * Ohne `send` entsteht nur der Entwurf; die Antwort enthält den fertigen Text,
 * damit ihn der Dialog zeigen kann. Mit `send: true` geht er im selben Zug raus
 * - der Betreiber hat ihn dann im Dialog gesehen und bestätigt, ein zweiter
 * Weg über den Postausgang wäre reiner Umweg (Entscheidung 18.08.2026).
 *
 * Ein zweiter Klick liefert denselben Entwurf zurück (`created: false`),
 * erzeugt also keine zweite Mail.
 */
export function useCreateMaintenanceReportMail(): ReturnType<
  typeof useMutation<MaintenanceReportMailResult, Error, { sessionId: string; send?: boolean }>
> {
  const qc = useQueryClient();
  return useMutation<MaintenanceReportMailResult, Error, { sessionId: string; send?: boolean }>({
    mutationFn: ({ sessionId, send }) =>
      api.post<MaintenanceReportMailResult, { send: boolean }>(
        `/maintenance-sessions/${sessionId}/report-mail`,
        { send: send ?? false }
      ),
    onSuccess: (_res, vars) => {
      // Der Zähler "berichte_erzeugt" am Fenster ist jetzt veraltet ...
      void qc.invalidateQueries({ queryKey: maintenanceHistoryKeys.detail(vars.sessionId) });
      // ... und im Postausgang liegt ein neuer Entwurf.
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

// --- Manuelle Nacherfassung --------------------------------------------------

export interface ManuelleWartungInput {
  customerId: string;
  /** ISO-8601 MIT Zeitzonen-Offset (siehe Dialog). */
  von: string;
  bis: string;
  systemIds: string[];
  arbeitsbeschreibung: string;
  durchgefuehrtVon: string;
  benachrichtigen: boolean;
  /**
   * true = die gewählten Systeme bekommen den neuen Wartungsstand.
   * Reichweite: bei Kunden ohne Apply-Run rechnet die Fälligkeit über
   * MAX(last_maintenance_at) je Kunde - ein einziges Nachtragen setzt dann
   * den ganzen Kunden auf "gewartet".
   */
  wartungsstandSetzen: boolean;
}

export interface ManuelleWartungResult {
  session: MaintenanceSessionHistoryItem;
  systeme_aktualisiert: number;
  mail_erzeugt: boolean;
  notification_id: string | null;
  mail_status: string | null;
  recipients: string[];
  effective_recipients: string[];
  subject: string | null;
  body: string | null;
}

interface ManuelleWartungBody {
  von: string;
  bis: string;
  system_ids: string[];
  arbeitsbeschreibung: string;
  durchgefuehrt_von: string;
  benachrichtigen: boolean;
  wartungsstand_setzen: boolean;
}

/**
 * Nacherfassen einer von Hand durchgeführten Wartung.
 *
 * Invalidiert Historie, aktuelles Fenster, Kundendetail (last_maintenance_at
 * der Systeme) und den Postausgang - analog zu Open/Close, plus die
 * Historienliste, weil dort die neue Zeile erscheinen muss.
 */
export function useErfasseManuelleWartung(): ReturnType<
  typeof useMutation<ManuelleWartungResult, Error, ManuelleWartungInput>
> {
  const qc = useQueryClient();
  return useMutation<ManuelleWartungResult, Error, ManuelleWartungInput>({
    mutationFn: ({
      customerId,
      von,
      bis,
      systemIds,
      arbeitsbeschreibung,
      durchgefuehrtVon,
      benachrichtigen,
      wartungsstandSetzen,
    }) =>
      api.post<ManuelleWartungResult, ManuelleWartungBody>(
        `/customers/${customerId}/maintenance-sessions/manual`,
        {
          von,
          bis,
          system_ids: systemIds,
          arbeitsbeschreibung,
          durchgefuehrt_von: durchgefuehrtVon,
          benachrichtigen,
          wartungsstand_setzen: wartungsstandSetzen,
        }
      ),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: maintenanceHistoryKeys.byCustomer(vars.customerId) });
      void qc.invalidateQueries({ queryKey: maintenanceSessionKeys.byCustomer(vars.customerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.customers.detail(vars.customerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}
